import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as LoadWalletIcon } from "../../assets/images/icons/billing_overview/load_wallet.svg";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Divider,
  OutlinedInput,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp,
  CreditCard,
  Message,
  AccountBalanceWallet,
  Public,
  Receipt,
  Settings,
  Download,
  Add,
  Visibility,
  CalendarToday,
  AttachMoney,
  ArrowUpward,
  Info,
} from "@mui/icons-material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { GET_WALLET_DETAILS } from "../../config/urls";
import { GET_WALLET_STATS } from "../../config/urls";
import { GET_WALLET_SUMMARY } from "../../config/urls";
import { GET_WALLET_MONTHLY_SPEND } from "../../config/urls";
import { GET_WALLET_TRANSACTIONS } from "../../config/urls";
import { GET_WALLET_ADD_FUNDS } from "../../config/urls";
import { GET_WALLET_INITIATE_CASHFREE_ENDPOINT } from "../../config/urls";
import { GET_TENANT_DETAILS } from "../../config/urls";

import { load } from "@cashfreepayments/cashfree-js";
import { CASHFREE_MODE } from "../../config/environment";
import { useAuth } from "../../hooks/AuthProvider";
import { ColorsConstants } from "../../cssUtils/constants";
import CustomTabs from "../../common/custom-tabs";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import typography from "../../cssUtils/typography";
import { formatWithTzLabel } from "../../utils/dateUtils";

export default function WalletTab({ onNavigate }) {
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [monthlySpend, setMonthlySpend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [submitting, setSubmitting] = useState(false);
  const [billingCurrency, setBillingCurrency] = useState("INR"); // Default to INR

  const { getValidToken } = useAuth();

  // Function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "INR":
        return "₹";
      case "AED":
        return "د.إ";
      default:
        return "₹"; // Default to Indian Rupee symbol
    }
  };

  // Function to format currency with proper symbol
  const formatCurrency = (value) => {
    const currencySymbol = getCurrencySymbol(billingCurrency);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: billingCurrency,
      minimumFractionDigits: 2,
    })
      .format(value)
      .replace(/[A-Z]{3}/, currencySymbol);
  };

  // Simple format for display without full currency formatting
  const formatCurrencySimple = (value) => {
    const currencySymbol = getCurrencySymbol(billingCurrency);
    return `${currencySymbol}${value ? parseFloat(value).toFixed(2) : "0.00"}`;
  };

  // Fetch wallet data on component mount
  useEffect(() => {
    fetchWalletData();
    fetchTransactions(1);
    fetchMonthlySpend();
    fetchTenantDetails(); // Fetch tenant details for currency
  }, []);

  const apiRequest = async (url, options = {}) => {
    const token = await getValidToken();
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": localStorage.getItem("tenant"),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  };

  // Fetch tenant details to get billing currency
  const fetchTenantDetails = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(GET_TENANT_DETAILS, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-ID": tenant,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Tenant Details:", data);

      // Set billing currency from tenant details
      if (
        data.results &&
        data.results.length > 0 &&
        data.results[0].billing_currency
      ) {
        setBillingCurrency(data.results[0].billing_currency);
      }
    } catch (error) {
      console.error("Error fetching tenant details:", error);
    }
  };

  const walletAPI = {
    getWalletDetails: () => apiRequest(GET_WALLET_DETAILS),

    getWalletStats: () => apiRequest(GET_WALLET_STATS),

    getTransactions: (page = 1, pageSize = 20) =>
      apiRequest(
        `${GET_WALLET_TRANSACTIONS}?page=${page}&page_size=${pageSize}`,
      ),

    getMonthlySpend: () => apiRequest(GET_WALLET_MONTHLY_SPEND),

    addFunds: (amount, paymentMethod = "BANK_TRANSFER") =>
      apiRequest(GET_WALLET_ADD_FUNDS, {
        method: "POST",
        body: JSON.stringify({
          amount: amount,
          payment_method: paymentMethod,
        }),
      }),
  };

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const data = await walletAPI.getWalletStats();
      console.log("Wallet data fetched:", data);
      setWalletData(data);
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      showSnackbar("Error loading wallet data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page = 1, pageSize = rowsPerPage) => {
    try {
      setTransactionsLoading(true);
      const response = await walletAPI.getTransactions(page, pageSize);
      setTransactions(response.results || []);
      setDataCount(response.count || 0);
      setTotalPages(Math.ceil(response.count / pageSize));
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      showSnackbar("Error loading transactions", "error");
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchMonthlySpend = async () => {
    try {
      const data = await walletAPI.getMonthlySpend();
      console.log("Monthly spend fetched:", data);
      setMonthlySpend(data?.current_month_spend || 0);
    } catch (error) {
      console.error("Error fetching monthly spend:", error);
      showSnackbar("Error loading monthly spend", "error");
    }
  };

  const handlePageChange = (event, page) => {
    fetchTransactions(page);
  };

  const handleAddFunds = () => {
    setShowAddFundsModal(true);
  };

  const handleAmountSubmit = () => {
    if (amount && parseFloat(amount) > 0) {
      setShowAddFundsModal(false);
      setShowConfirmModal(true);
    }
  };

  let cashfree = null;

  const cashfreeRef = useRef(null);
  useEffect(() => {
    const initializeSDK = async () => {
      if (typeof window === "undefined") return;

      try {
        const cf = await load({ mode: CASHFREE_MODE });
        if (!cf) {
          console.error("❌ SDK load() returned null");
          return;
        }
        console.log("✅ SDK loaded", cf);
        cashfreeRef.current = cf; // ✅ Store it in the ref
      } catch (e) {
        console.error("❌ Failed to load SDK:", e);
      }
    };

    initializeSDK();
  }, []);

  // ____________________________________________________________________________________________________________
  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      const tenant = localStorage.getItem("tenant");
      const token = await getValidToken();

      const res = await fetch(`${GET_WALLET_INITIATE_CASHFREE_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
        body: JSON.stringify({ amount: parseInt(amount, 10) }),
      });

      const data = await res.json();
      console.log("init", cashfree);
      console.log("Initiate Cashfree response:", data);
      console.log("Cashfree ref:", cashfreeRef.current);
      if (data.payment_session_id && cashfreeRef.current) {
        try {
          cashfreeRef.current.checkout({
            paymentSessionId: data.payment_session_id,
            redirectTarget: "_self",
          });
        } catch (err) {
          console.error("Cashfree checkout error:", err);
          showSnackbar("Failed to open payment window", "error");
        }
      } else {
        showSnackbar(
          data.error || data.amount || "Failed to initiate payment.",
          "error",
        );
      }
    } catch (error) {
      console.error("Error initiating payment:", error);
      showSnackbar("Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowAddFundsModal(false);
    setShowConfirmModal(false);
    setAmount("");
  };
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return { bgcolor: "#e8f5e8", color: "#2e7d32" };
      case "pending":
        return { bgcolor: "#fff3e0", color: "#f57c00" };
      case "failed":
        return { bgcolor: "#ffebee", color: "#d32f2f" };
      default:
        return { bgcolor: "#f5f5f5", color: "#666" };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const tableColumns = [
    {
      id: "created_at",
      label: "Date",
      render: (transaction) => (
        <span style={{ color: "text.secondary" }}>
          {formatWithTzLabel(transaction.created_at, "MMM D, YYYY")}
        </span>
      ),
    },
    {
      id: "type_display",
      label: "Type",
      render: (transaction) => (
        <span style={{ fontWeight: 500 }}>{transaction.type_display}</span>
      ),
    },
    {
      id: "method_display",
      label: "Method",
      render: (transaction) => (
        <span style={{ color: ColorsConstants.primaryBlue100 }}>
          {transaction.method_display}
        </span>
      ),
    },
    {
      id: "amount",
      label: "Amount",
      render: (transaction) => {
        const isCompleted = transaction.status_display?.toLowerCase() === "completed";
        const isDeposit   = transaction.type_display?.toLowerCase()   === "deposit";
        return (
          <span
            style={{
              fontWeight: 600,
              color: isCompleted
                ? isDeposit
                  ? "#2e7d32"  // green — completed deposit
                  : "#d32f2f"  // red   — completed debit
                : "#000000",   // black — pending / failed
            }}
          >
            {isCompleted
              ? isDeposit
                ? `+${formatCurrency(transaction.amount)}`
                : `-${formatCurrency(transaction.amount)}`
              : formatCurrency(transaction.amount)}
          </span>
        );
      },
    },
    {
      id: "status_display",
      label: "Status",
      render: (transaction) => (
        <Chip
          label={transaction.status_display}
          size="small"
          sx={{ ...getStatusColor(transaction.status), fontWeight: 600 }}
        />
      ),
    },
  ];

  const tabs = [
    {
      label: "Message Wallet",
      value: 0,
    },
    {
      label: "AI Credits Wallet",
      value: 1,
    },
  ];
  const currencySymbol = getCurrencySymbol(billingCurrency);
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  const handleCustomPageChange = (event, newPage) => {
    handlePageChange(event, newPage + 1); // convert 0-based → 1-based
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    fetchTransactions(1, newRowsPerPage);
  };

  return (
    <Box sx={{ pt: 5, px: 3, backgroundColor: "#F7F7F7" }}>
      {walletData?.unpaid_invoice && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            alignItems: "center",
            "& .MuiAlert-message": { flex: "unset" },
            "& .MuiAlert-action": { paddingTop: 0, marginLeft: "auto" },
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => onNavigate("billing_invoices")}
            >
              View Invoices
            </Button>
          }
        >
          You have an unpaid invoice. Please clear it to avoid service interruption.
        </Alert>
      )}
      <Box sx={{ mb: 2 }}>
        <CustomTabs
          value={currentTab}
          onChange={handleTabChange}
          tabs={tabs}
          inactiveBgColor="#E8EAED"
          backgroundColor="#E8EAED"
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{ ...typography.h1}}
            >
              Message Wallet
            </Typography>
            <Typography
              sx={{ ...typography.bodyLarge, color: "#737373", fontSize: "14px", }}
            >
              This shows your current plan, billing cycle, and subscription
              validity.
            </Typography>
          </Box>
          <Box>
            <ButtonOne
              sx={{
                borderColor: "#1976D2",
                color: "#ffffff",
                "&:hover": {
                  borderColor: "#1976D2",
                },
              }}
              startIcon={<LoadWalletIcon />}
               onClick={handleAddFunds}
            >
              Load Wallet
            </ButtonOne>
          </Box>
        </Box>

        <Box
          sx={{
            overflow: "hidden",
            width: "100%",
          }}
        > 
          <Grid container spacing={3} wrap="wrap">
            <Grid item xs={12} md={3}>
              <Card sx={{ pb: 3, boxShadow: "none"}}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ ...typography.bodyLarge }}
                      >
                        Available Balance
                      </Typography>
                      <Typography
                        variant="h4"
                        component="div"
                        fontWeight="semibold"
                        sx={{ ...typography.cost_text}}
                      >
                        {walletData
                          ? formatCurrency(walletData.balance)
                          : formatCurrencySimple(0)}
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{ bgcolor: "success.main", width: 56, height: 56 }}
                    >
                      <AccountBalanceWallet />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ pb: 3, boxShadow: "none"}}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ ...typography.bodyLarge }}
                        gutterBottom
                      >
                        Last Deposit
                      </Typography>
                      <Typography
                        variant="h4"
                        component="div"
                        fontWeight="semibold"
                         sx={{ ...typography.cost_text}}
                        
                      >
                        {walletData
                          ? formatCurrency(walletData.last_deposit_amount || 0)
                          : formatCurrencySimple(0)}
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{ bgcolor: "primary.main", width: 56, height: 56 }}
                    >
                      <Add />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ pb: 3, boxShadow: "none"}}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ ...typography.bodyLarge }}
                        gutterBottom
                      >
                        Last Month Spend
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="semibold"
                        sx={{ ...typography.cost_text}}>
                        {walletData
                          ? formatCurrency(walletData.monthly_spend)
                          : formatCurrencySimple(0)}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: "warning.main", width: 56, height: 56 }}>
                      <TrendingUp />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ pb: 3, boxShadow: "none" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ ...typography.bodyLarge }}
                        gutterBottom
                      >
                        Current Month Spend
                      </Typography>
                      <Typography 
                        fontSize={22}
                        sx={{ ...typography.cost_text}}
                        >
                        {monthlySpend !== null
                          ? formatCurrency(monthlySpend)
                          : formatCurrencySimple(0)}
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{ bgcolor: "secondary.main", width: 56, height: 56 }}
                    >
                      <AttachMoney />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Quick Top Up - Compact */}
      <Card sx={{ mb: 3, borderBottom: 0, boxShadow: "none" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography sx={{ ...typography.h1}}>Quick Top Up</Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              onClick={() => {
                setAmount("5000");
                setShowConfirmModal(true);
              }}
              sx={{ ...typography.bodyLarge, bgcolor: "#F7F8FA", color: ColorsConstants.primaryBlue100, py: 1, height: "auto", px: 2, border: "1px solid #CDCDCD", borderRadius: "8px" }}
            >
              ₹5,000
            </Button>
            <Button
              onClick={() => {
                setAmount("10000");
                setShowConfirmModal(true);
              }}
              sx={{ ...typography.bodyLarge, bgcolor: "#F7F8FA", color: ColorsConstants.primaryBlue100, py: 1, height: "auto", px: 2, border: "1px solid #CDCDCD", borderRadius: "8px" }}
            >
              ₹10,000
            </Button>
            <Button
              onClick={() => {
                setAmount("25000");
                setShowConfirmModal(true);
              }}
              sx={{ ...typography.bodyLarge, bgcolor: "#F7F8FA", color: ColorsConstants.primaryBlue100, py: 1, height: "auto", px: 2, border: "1px solid #CDCDCD", borderRadius: "8px" }}
            >
              ₹25,000
            </Button>
            <Button
              onClick={handleAddFunds}
              sx={{ ...typography.bodyLarge, bgcolor: "#F7F8FA", color: ColorsConstants.primaryBlue100, py: 1, height: "auto", px: 2, borderRadius: "8px" }}
            >
              Custom Amount
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card sx={{  boxShadow: "none"}}>
        <CardContent>
          <Typography sx={{ ...typography.h1, mb: 2}}>
            Recent Activity
          </Typography>
          <AppTable
            columns={tableColumns}
            rows={transactions}
            rowKey="id"
            emptyMessage="No transactions found"
            paperSx={{ border: "1px solid rgba(0,0,0,0.12)", boxShadow: "none", mb: 0 }}
            stickyHeader={false}
            hideMainScroll={false}
            // CustomPagination props — page is 0-based, count is total rows
            count={dataCount}
            page={currentPage - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleCustomPageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />

        </CardContent>
      </Card>
      {/* Add Funds Modal */}
      <Dialog
        open={showAddFundsModal}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Add Funds
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Amount to Add"
            type="number"
            fullWidth
            variant="outlined"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
            }}
            sx={{ mt: 2 }}
            helperText="Enter the amount you want to add to your wallet"
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleAmountSubmit}
            variant="contained"
            disabled={!amount || parseFloat(amount) <= 0}
            sx={{ ml: 2 }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog
        open={showConfirmModal}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Confirm Transaction
          <IconButton onClick={handleCancel} size="small" disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Are you sure you want to add the following amount to your wallet?
          </Typography>
          <Card sx={{ bgcolor: "#f8f9fa" }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Amount to Add
              </Typography>
              <Typography
                variant="h4"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                {amount && formatCurrency(parseFloat(amount))}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Payment Method: Bank Transfer
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            fullWidth
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              ml: 2,
              bgcolor: "#2e7d32",
              "&:hover": { bgcolor: "#1b5e20" },
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              "Confirm & Add Funds"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
